import { Box, ButtonBase, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { useCallback, useMemo } from "react";

/**
 * FilterTabSwitcher
 *
 * @param {string|number} value
 * @param {function} onChange
 * @param {Array<string|{label: string, value: string|number, count?: number, icon?: React.ReactNode}>} tabs
 * @param {number|string} fontSize
 * @param {string} activeColor
 * @param {string} activeBackgroundColor
 * @param {number|string|object} buttonPadding
 * @param {"start"|"end"} iconPosition
 */
const FilterTabSwitcher = ({
  value,
  onChange,
  tabs = [],
  fontSize = 13,
  activeColor = grey[800],
  activeBackgroundColor = "#fff",
  buttonPadding = { px: 1.75, py: 0.75 },
  iconPosition = "start",
}) => {
  const normalizedTabs = useMemo(() => {
    return (tabs || []).map((t) => {
      if (typeof t === "string") {
        return { label: t, value: t };
      }
      return t;
    });
  }, [tabs]);

  const handleClick = useCallback(
    (e, tabValue) => {
      if (tabValue !== value) {
        onChange?.(tabValue);
      }
    },
    [value, onChange],
  );

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        bgcolor: grey[100],
        borderRadius: "10px",
        p: "4px",
      }}
    >
      {normalizedTabs.map((tab) => {
        const isSelected = tab.value === value;
        const textColor = isSelected ? activeColor : grey[500];

        return (
          <ButtonBase
            key={tab.value}
            onClick={(e) => handleClick(e, tab.value)}
            disableRipple
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              borderRadius: "8px",
              transition: "all 0.15s ease",
              bgcolor: isSelected ? activeBackgroundColor : "transparent",
              boxShadow: isSelected ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
              ...(typeof buttonPadding === "object"
                ? buttonPadding
                : { p: buttonPadding }),
              "&:hover": {
                bgcolor: isSelected ? activeBackgroundColor : grey[200],
              },
            }}
          >
            {tab.icon && iconPosition === "start" && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: textColor,
                  svg: { fontSize: fontSize * 1.25 },
                }}
              >
                {tab.icon}
              </Box>
            )}

            <Typography
              sx={{
                fontSize,
                fontWeight: 500,
                lineHeight: 1,
                color: textColor,
                whiteSpace: "nowrap",
                userSelect: "none",
                display: "flex",
                alignItems: "center",
              }}
            >
              {tab.label}
              {tab.count != null && (
                <Typography
                  component="span"
                  sx={{
                    fontSize,
                    fontWeight: 500,
                    color: textColor,
                    opacity: isSelected ? 0.6 : 1,
                    // color: isSelected ? grey[500] : grey[400],
                    ml: 0.5,
                  }}
                >
                  ({tab.count})
                </Typography>
              )}
            </Typography>

            {tab.icon && iconPosition === "end" && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: textColor,
                }}
              >
                {tab.icon}
              </Box>
            )}
          </ButtonBase>
        );
      })}
    </Box>
  );
};

export default FilterTabSwitcher;
